/**
 * The in-flight passkey attempt, and whether a continuation still belongs to the
 * mount that started it.
 *
 * Split out of `use-passkey-sign-in.ts` because it answers one question the rest
 * of that state machine only consumes: this hook owns the abort controller, the
 * wall-clock cap and the mount epoch, and nothing else may reach past it to
 * decide that a stale continuation is live.
 */

import { useEffect, useEffectEvent, useRef } from 'react';

import type { CeremonyDeadline } from '@/components/auth/passkey-deadlines';
import type { RefObject } from 'react';

export interface PasskeyAttemptLifecycleOptions {
  /**
   * Runs on every mount, including the second half of a remount that PRESERVED
   * state (`<Activity>`, StrictMode) — where the instance comes back holding
   * whatever the last teardown froze.
   */
  readonly onRemount: () => void;
  /**
   * Runs on every teardown, AFTER the ceremony has been aborted and forgotten.
   * The place to compensate a session the flow can no longer discharge in the UI.
   */
  readonly onTeardown: () => void;
}

export interface PasskeyAttemptLifecycle {
  /** The ceremony in flight. Doubles as the single-flight latch. */
  readonly ceremonyRef: RefObject<AbortController | null>;
  /** The current attempt's cap, so a teardown can drop it. Never shared. */
  readonly deadlineRef: RefObject<CeremonyDeadline | null>;
  /** Captured at the top of anything that can outlive the mount it started in. */
  readonly currentEpoch: () => number;
  /** Whether a continuation still belongs to the mount that captured `epoch`. */
  readonly isCurrent: (epoch: number) => boolean;
}

export function usePasskeyAttemptLifecycle(
  options: PasskeyAttemptLifecycleOptions,
): PasskeyAttemptLifecycle {
  const ceremonyRef = useRef<AbortController | null>(null);
  const deadlineRef = useRef<CeremonyDeadline | null>(null);
  const unmountedRef = useRef(false);
  /**
   * Bumped once per MOUNT. `unmountedRef` alone cannot stand in for it: that ref
   * answers "is this instance torn down", which is false again the moment a
   * preserved-state remount brings the instance back — so a continuation from
   * BEFORE the teardown reads as live and walks into the success branch of a
   * ceremony the cleanup already aborted.
   */
  const mountEpochRef = useRef(0);

  // Created here rather than accepted as one: an effect event may be called from
  // inside an Effect but never handed across a hook boundary.
  const remount = useEffectEvent(options.onRemount);
  const teardown = useEffectEvent(options.onTeardown);

  useEffect(() => {
    mountEpochRef.current += 1;
    // Reset, not just set-on-cleanup: a remount reuses the same ref object. A ref
    // left pinned `true` makes every continuation early-return AND turns the
    // abort branches into a revoke over a live, wanted session.
    unmountedRef.current = false;
    remount();

    return () => {
      unmountedRef.current = true;
      // A `navigator.credentials.get()` promise pends for minutes and settles on
      // a user gesture. Left alone, a user who leaves the page mid-sheet and then
      // completes it lands a session with no step-up UI mounted anywhere.
      ceremonyRef.current?.abort();
      // Forgotten as well as aborted. The ref IS the single-flight latch, and it
      // survives a preserved-state remount — left set, it refuses every future
      // press for the lifetime of the instance, which is the exact latch the
      // wall-clock cap exists to break. Identity, not the unmount flag, is what
      // the continuations re-check, so dropping it here is safe.
      ceremonyRef.current = null;
      // The cap exists to release THIS page's controls, and there are none left
      // to release. Whatever the abort above could not stop still lands on the
      // awaited race, where the session guard compensates.
      deadlineRef.current?.cancel();
      deadlineRef.current = null;
      teardown();
    };
  }, []);

  return {
    ceremonyRef,
    deadlineRef,
    currentEpoch: () => mountEpochRef.current,
    isCurrent: (epoch: number) => !unmountedRef.current && mountEpochRef.current === epoch,
  };
}
